import { useCallback, useMemo } from 'react';
import { useGetReleasePlanQuery } from '../../../../../../redux/api/fymAPI';
import type { ReleasePlan } from '../../../../../../redux/api/fymAPI.types';
import { useRealtimePollInterval } from '../../../../../realtime/useRealtimePollInterval';
import type { SocialPlatform } from '../../../types';
import { browserTimeZone, dayInZone } from '../rollout/rolloutTime';

/** The poll of a plan whose reels are made, only while the realtime socket is down. */
const MAKING_POLL_MS = 5000;

interface UseReelsPlanAnswerProps {
  accountId: number;
  catalogItemUuid: string;
  /** The usable channels of the artist. */
  platforms: SocialPlatform[];
  skip: boolean;
}

export interface ReelsPlanAnswer {
  /** `GET releases/:uuid/plan?kind=reels`; undefined while it loads and after an error. */
  plan: ReleasePlan | undefined;
  isLoading: boolean;
  error: ReturnType<typeof useGetReleasePlanQuery>['error'];
  retry: () => void;
  /** `YYYY-MM-DD` of today in the time zone of the artist (the zone of the viewer when none is stored). */
  today: string;
}

/**
 * The AI reels plan of a song as the Auto campaign tab reads it (design rollout-plan f3). The events
 * `reel.updated`, `promo.posts.changed` and `promo.post.updated` read it again
 * (`realtimeEventTags.ts`). It polls only while a reel is made and the socket is down.
 */
export function useReelsPlanAnswer({
  accountId,
  catalogItemUuid,
  platforms,
  skip,
}: UseReelsPlanAnswerProps): ReelsPlanAnswer {
  const timeZone = useMemo(() => browserTimeZone(), []);
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, kind: 'reels' as const, platforms, timeZone }),
    [accountId, catalogItemUuid, platforms, timeZone]
  );
  const query = useGetReleasePlanQuery(args, { skip });
  const days = query.data?.days;
  const isMaking = useMemo(() => Array.isArray(days) && days.some((day) => day.asset?.status === 'working'), [days]);
  // A second subscription with polling only while a reel is made.
  const pollingInterval = useRealtimePollInterval(MAKING_POLL_MS);
  useGetReleasePlanQuery(args, { skip: skip || !isMaking, pollingInterval });

  // The server counts the days of a plan in the time zone of the artist (`timeZone` of the answer).
  // "Today", the default start day and the first day of the date field use the same zone.
  const artistZone = query.data?.timeZone ?? null;
  const today = useMemo(() => dayInZone(new Date(), artistZone), [artistZone]);
  const { refetch } = query;
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  return { plan: query.data, isLoading: query.isLoading, error: query.error, retry, today };
}
